import { useContext, useEffect } from "react";
import Button from "src/components/Button";
import Field from "src/components/field/Field";
import Input from "src/components/form/Input";
import withTranslation, {
	Translation,
} from "src/components/hoc/withTranslation";
import Layout from "src/components/layout/Layout";
import { ModalViewContext } from "src/components/layout/ModalView";
import useTenant from "src/hooks/api/services/tenants/useTenant";
import useTasksFilter from "src/hooks/api/tasks/useTasksFilter";

function TasksFilterView({ t }: Translation) {
	const modal = useContext(ModalViewContext);
	const { filter, actions } = useTasksFilter();
	const { tenant } = useTenant();

	useEffect(() => {
		modal.setTitle(t("title"));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Layout title={t("title")}>
			<div className="flex flex-col gap-4">
				<Input
					autoFocus
					icon={<i className="far fa-search" />}
					name="search"
					placeholder={t("fields.search.placeholder")}
					value={filter.q}
					className="p-0"
					noMarginBottom
					onChange={(key, value) => actions.set({ q: value })}
				/>
				{tenant.modules.includes("intern-task") && (
					<>
						<Field.Options
							wrapper={{
								label: t("fields.type.label"),
								layout: "horizontal",
							}}
							onChange={(value) =>
								actions.set({
									type: value,
								})
							}
							value={filter.type}
							options={[
								{
									label: t("fields.type.options.all"),
									value: "all",
								},
								{
									label: t("fields.type.options.internal"),
									value: "internal",
								},
								{
									label: t("fields.type.options.external"),
									value: "published",
								},
							]}
						/>

						<Field.Users
							wrapper={{
								label: t("fields.assignees.label"),
								layout: "horizontal",
							}}
							value={
								filter.assignees
									? filter.assignees.map((user: any) =>
											typeof user === "object"
												? user.id.toString()
												: user
									  )
									: []
							}
							onChange={(value) => {
								actions.set({
									assignees: value,
								});
							}}
						/>
					</>
				)}

				<Field.User
					isClearable
					wrapper={{
						label: t("fields.creator.label"),
						layout: "horizontal",
					}}
					value={filter.creator_id ? filter.creator_id : undefined}
					onChange={(value) =>
						actions.set({
							creator_id: value,
						})
					}
				/>
				<Field.Connection
					isClearable
					wrapper={{
						label: t("fields.match.label"),
						layout: "horizontal",
					}}
					value={
						filter.match_student_id
							? filter.match_student_id
							: undefined
					}
					onChange={(value) => {
						actions.set({
							match_student_id: value,
						});
					}}
				/>
				{tenant.modules.includes("intern-task") && (
					<>
						<Field.Team
							wrapper={{
								label: t("fields.team.label"),
								layout: "horizontal",
							}}
							value={filter.teams ? filter.teams : undefined}
							onChange={(value) => {
								actions.set({
									teams: value,
								});
							}}
						/>
						<Field.Tag
							wrapper={{
								label: t("fields.tag.label"),
								layout: "horizontal",
							}}
							value={filter.tags ? filter.tags : undefined}
							onChange={(value) => {
								actions.set({
									tags: value,
								});
							}}
						/>
					</>
				)}
			</div>
			<div className="mt-12">
				<Button
					type="dark"
					className="w-full"
					onClick={() => modal.onClose()}
				>
					{t("apply")}
				</Button>
			</div>
		</Layout>
	);
}

TasksFilterView.locale = {
	nl: {
		title: "Taken filteren",
		apply: "Toepassen",
		fields: {
			type: {
				label: "Type",
				options: {
					all: "Alles",
					internal: "Intern",
					external: "Uitbesteed",
				},
			},
			search: {
				placeholder: "Zoek een taak...",
			},
			match: {
				label: "Match",
			},
			creator: {
				label: "Aangemaakt door",
			},
			tag: {
				label: "Tags",
			},
			team: {
				label: "Team",
			},
			assignees: {
				label: "Uitvoerder",
			},
		},
	},
	en: {
		title: "Filter tasks",
		apply: "Apply",
		fields: {
			type: {
				label: "Type",
				options: {
					all: "All",
					internal: "Intern",
					external: "Outsourced",
				},
			},
			search: {
				placeholder: "Search a task...",
			},
			match: {
				label: "Match",
			},
			creator: {
				label: "Created by",
			},
			tag: {
				label: "Tags",
			},
			assignees: {
				label: "Assigned to",
			},
		},
	},
};

export default withTranslation(TasksFilterView);
